import React, { useState } from "react";
import {
  Box,
  Button,
  Drawer,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import { useAppDispatch } from "redux/hooks";
import {
  setSelectedField,
  updateFieldInSignRequest,
  updateFieldOfSelectedFile,
} from "redux/appSlice";
import { FileField } from "models/interfaces";
import { capitalizeText } from "helpers/methods";
import { pressableProps } from "../mobileA11y";

const FONT_FAMILIES = ["Times", "Courier", "Helvetica"];
const FONT_SIZES = [12, 14, 16, 18, 20, 22, 24, 26, 28, 36, 48, 72];
// Text-color swatches (picker data — the value stored on the field, not UI
// theming). Matches the Figma Formatting palette.
const COLORS = [
  "#0E091E",
  "#2200FF",
  "#CE150A",
  "#009154",
  "#FFB020",
  "#7F00FF",
  "#E78A85",
];

type FontWeight = { bold: boolean; italic: boolean; underline: boolean };

/**
 * "Formatting" properties sheet (Figma node 1346-7341). Opens from the
 * "Formatting" chip of the field-options bar for text-family fields.
 *
 * Uses the SAME update logic as the desktop PropertiesDrawer: each control
 * applies its change live via `updateFieldOfSelectedFile` +
 * `updateFieldInSignRequest` + `setSelectedField`. (Desktop also bumps `field.h`
 * to the font size in px; that is desktop-pixel geometry and is intentionally
 * omitted here because mobile stores `h` as a page percentage.)
 */
const MobileFieldFormattingSheet = ({
  open,
  onClose,
  field,
}: {
  open: boolean;
  onClose: () => void;
  field: FileField | null;
}) => {
  const theme = useTheme();
  const m = theme.palette.mobile;
  const dispatch = useAppDispatch();

  const [fontAnchor, setFontAnchor] = useState<null | HTMLElement>(null);
  const [sizeAnchor, setSizeAnchor] = useState<null | HTMLElement>(null);

  const styles = (field?.styles || {}) as any;
  const fontFamily: string =
    styles.fontFamily && styles.fontFamily !== "none" ? styles.fontFamily : "";
  const size: number = styles.size || 16;
  const color: string = styles.color || "#1A1A1A";
  const weight: FontWeight = {
    bold: !!styles.fontWeight?.bold,
    italic: !!styles.fontWeight?.italic,
    underline: !!styles.fontWeight?.underline,
  };

  // Desktop-style live style update: patch styles, then dispatch to the file,
  // the sign request, and the selected field (mirrors updateSelectedFieldStyles
  // / handleFontWightClick in PropertiesDrawer).
  const applyStyles = (patchStyles: Record<string, any>) => {
    if (!field) return;
    const newStyles = { ...field.styles, ...patchStyles };
    const pdfField: any = { id: field.id, styles: newStyles };
    dispatch(updateFieldOfSelectedFile(pdfField));
    dispatch(updateFieldInSignRequest(pdfField));
    dispatch(setSelectedField({ ...field, styles: newStyles }));
  };

  const toggleWeight = (key: keyof FontWeight) =>
    applyStyles({ fontWeight: { ...weight, [key]: !weight[key] } });

  const handleClear = () =>
    applyStyles({
      fontFamily: "Times",
      size: 16,
      color: "#1A1A1A",
      fontWeight: { bold: false, italic: false, underline: false },
    });

  const previewText = field?.label || capitalizeText(field?.type || "Text");

  const styleButtons: { key: keyof FontWeight; icon: React.ReactNode }[] = [
    { key: "bold", icon: <FormatBoldIcon sx={{ fontSize: 18 }} /> },
    { key: "underline", icon: <FormatUnderlinedIcon sx={{ fontSize: 18 }} /> },
    { key: "italic", icon: <FormatItalicIcon sx={{ fontSize: 18 }} /> },
  ];

  const sectionLabel = {
    fontSize: "0.75rem",
    fontWeight: 500,
    color: m.base[900],
    mb: 1,
  } as const;

  const dropdownBox = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 1,
    border: `1px solid ${m.grey[50]}`,
    borderRadius: "8px",
    p: 2,
    cursor: "pointer",
  } as const;

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { px: 2, pb: 3, borderTopLeftRadius: 0, borderTopRightRadius: 0 },
        role: "dialog",
        "aria-modal": true,
        "aria-labelledby": "field-formatting-sheet-title",
      }}
    >
      {/* Drag handle */}
      <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5, pb: 1.5 }}>
        <Box
          aria-hidden="true"
          sx={{ width: 42, height: 4, borderRadius: 2, bgcolor: m.grey[50] }}
        />
      </Box>

      {/* Title + Clear */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography
          id="field-formatting-sheet-title"
          component="h2"
          sx={{ fontSize: "1rem", fontWeight: 600, color: m.base[900] }}
        >
          Formatting
        </Typography>
        <Typography
          {...pressableProps(handleClear, { label: "Reset formatting" })}
          sx={{
            fontSize: "0.875rem",
            fontWeight: 700,
            color: m.grey[400],
            cursor: "pointer",
          }}
        >
          Clear
        </Typography>
      </Box>

      {/* Live preview card */}
      <Box
        sx={{
          border: `1px solid ${m.grey[50]}`,
          borderRadius: "8px",
          p: 2,
          mb: 2.5,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            minWidth: 96,
            maxWidth: "100%",
            border: `1px dashed ${m.red[500]}`,
            borderRadius: "8px",
            px: 1.5,
            py: 1,
            textAlign: "center",
            boxShadow: "0px 4px 4.45px rgba(8,3,26,0.15)",
            bgcolor: m.surface.white,
          }}
        >
          <Typography
            sx={{
              fontFamily: fontFamily || undefined,
              fontWeight: weight.bold ? 700 : 400,
              fontStyle: weight.italic ? "italic" : "normal",
              textDecoration: weight.underline ? "underline" : "none",
              color,
              fontSize: `${Math.min(Math.max(size, 10), 22)}px`,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {previewText}
          </Typography>
        </Box>
      </Box>

      {/* Font family + size */}
      <Box sx={{ display: "flex", gap: 1, mb: 2.5 }}>
        <Box
          sx={{ ...dropdownBox, flex: 1, minWidth: 0 }}
          {...pressableProps((e) => setFontAnchor(e.currentTarget as HTMLElement), {
            label: `Font family, ${fontFamily || "not selected"}`,
            hasPopup: "menu",
            expanded: Boolean(fontAnchor),
          })}
        >
          <Typography
            sx={{
              fontSize: "0.875rem",
              fontWeight: 500,
              color: fontFamily ? m.base[900] : m.grey[200],
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {fontFamily || "Select font"}
          </Typography>
          <KeyboardArrowDownIcon sx={{ fontSize: 18, color: m.base[500] }} />
        </Box>
        <Box
          sx={{ ...dropdownBox, width: 86, flexShrink: 0 }}
          {...pressableProps((e) => setSizeAnchor(e.currentTarget as HTMLElement), {
            label: `Font size, ${size}`,
            hasPopup: "menu",
            expanded: Boolean(sizeAnchor),
          })}
        >
          <Typography
            sx={{ fontSize: "0.875rem", fontWeight: 500, color: m.base[900] }}
          >
            {size}
          </Typography>
          <KeyboardArrowDownIcon sx={{ fontSize: 18, color: m.base[500] }} />
        </Box>
      </Box>

      <Menu
        anchorEl={fontAnchor}
        open={Boolean(fontAnchor)}
        onClose={() => setFontAnchor(null)}
        PaperProps={{ sx: { maxHeight: 260, minWidth: 200, borderRadius: 2 } }}
        MenuListProps={{ "aria-label": "Font family" }}
      >
        {FONT_FAMILIES.map((f) => (
          <MenuItem
            key={f}
            selected={f === fontFamily}
            onClick={() => {
              applyStyles({ fontFamily: f });
              setFontAnchor(null);
            }}
            sx={{ fontFamily: f, fontSize: "0.875rem", color: m.base[500] }}
          >
            {f}
          </MenuItem>
        ))}
      </Menu>

      <Menu
        anchorEl={sizeAnchor}
        open={Boolean(sizeAnchor)}
        onClose={() => setSizeAnchor(null)}
        PaperProps={{ sx: { maxHeight: 260, minWidth: 96, borderRadius: 2 } }}
        MenuListProps={{ "aria-label": "Font size" }}
      >
        {FONT_SIZES.map((sz) => (
          <MenuItem
            key={sz}
            selected={sz === size}
            onClick={() => {
              applyStyles({ size: sz });
              setSizeAnchor(null);
            }}
            sx={{ fontSize: "0.875rem", color: m.base[500] }}
          >
            {sz}
          </MenuItem>
        ))}
      </Menu>

      {/* Style toggles: B / U / I */}
      <Box
        sx={{ display: "flex", gap: 0.5, mb: 2.5 }}
        role="group"
        aria-label="Text style"
      >
        {styleButtons.map((b) => {
          const active = weight[b.key];
          return (
            <Box
              key={b.key}
              {...pressableProps(() => toggleWeight(b.key), {
                label: capitalizeText(b.key),
                selected: active,
              })}
              sx={{
                width: 40,
                height: 40,
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                bgcolor: active ? m.ePurple[500] : m.ePurple[50],
                color: active ? m.surface.white : m.base[900],
              }}
            >
              {b.icon}
            </Box>
          );
        })}
      </Box>

      {/* Colors */}
      <Typography id="field-color-label" sx={sectionLabel}>
        Colors
      </Typography>
      <Box
        sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 2.5 }}
        role="group"
        aria-labelledby="field-color-label"
      >
        {COLORS.map((c) => {
          const selected = color.toLowerCase() === c.toLowerCase();
          return (
            <Box
              key={c}
              {...pressableProps(() => applyStyles({ color: c }), {
                label: `Text color ${c}`,
                selected,
              })}
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                bgcolor: c,
                cursor: "pointer",
                boxShadow: selected
                  ? `0 0 0 2px ${m.surface.white}, 0 0 0 4px ${m.ePurple[500]}`
                  : "none",
              }}
            />
          );
        })}
      </Box>

      {/* Update — changes apply live (desktop logic); this dismisses the sheet. */}
      <Button
        fullWidth
        variant="contained"
        onClick={onClose}
        sx={{
          mt: 0.5,
          bgcolor: m.ePurple[500],
          color: m.surface.white,
          textTransform: "none",
          fontSize: "0.875rem",
          fontWeight: 700,
          lineHeight: "20px",
          borderRadius: "8px",
          py: 2,
          boxShadow: "none",
          "&:hover": { bgcolor: m.ePurple[700], boxShadow: "none" },
        }}
      >
        Update
      </Button>
    </Drawer>
  );
};

export default MobileFieldFormattingSheet;
